import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { formatDate, calculateVATInclusiveAmount } from "../../lib/appUtils";
import { GlobalNav } from "../shared/GlobalNav";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  FileText, Plus, Copy, CheckCircle, Clock, Calendar,
  ArrowRight, AlertTriangle, Settings, Shield, Link,
  PiggyBank, Award, RefreshCw, Upload, Circle, Banknote, ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountingConnectionCard = ({ provider, name, description, color, connected }) => {
  const [connecting, setConnecting] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await axios.post(`${API}/integrations/${provider}/connect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.auth_url) {
        // Open OAuth flow in new window
        window.open(response.data.auth_url, '_blank', 'width=600,height=700');
        toast({
          title: "Opening Connection",
          description: `Complete the ${name} authorization in the new window`
        });
      }
    } catch (error) {
      toast({
        title: "Connection Error",
        description: `Failed to connect to ${name}`,
        variant: "destructive"
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    try {
      await axios.post(`${API}/integrations/${provider}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Sync Started",
        description: `${name} data sync initiated in background`
      });
    } catch (error) {
      toast({
        title: "Sync Error",
        description: `Failed to sync ${name} data`,
        variant: "destructive"
      });
    }
  };

  const handlePreview = () => {
    // Open preview modal showing exact client view
    setShowPreview(true);
  };

  const handleEdit = () => {
    // Open edit form for quote
    setShowEditForm(true);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quote.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quote-${quote.title.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "PDF Downloaded",
        description: "Quote PDF has been downloaded successfully"
      });
    } catch (error) {
      toast({
        title: "Download Error",
        description: "Failed to generate PDF",
        variant: "destructive"
      });
    }
  };

  return (
    <div className={`p-4 border-2 rounded-lg transition-all h-full flex flex-col ${
      connected ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:border-gray-300'
    }`}>
      <div className="text-center flex flex-col flex-grow">
        <div className={`w-12 h-12 ${color} rounded-lg mx-auto mb-3 flex items-center justify-center`}>
          <FileText className="w-6 h-6 text-white" />
        </div>
        <h4 className="font-semibold text-gray-900 font-heading">{name}</h4>
        <p className="text-xs text-gray-600 font-body mt-1 flex-grow">{description}</p>
        
        {connected ? (
          <div className="mt-4 space-y-2">
            <Badge className="bg-green-100 text-green-700 text-xs">Connected</Badge>
            {lastSync && (
              <p className="text-xs text-gray-500 font-body">
                Last sync: {new Date(lastSync).toLocaleDateString()}
              </p>
            )}
            <div className="flex space-x-1">
              <Button
                onClick={handleSync}
                size="sm"
                variant="outline"
                className="text-xs font-body flex-1"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Sync
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-xs text-red-600 hover:text-red-700 font-body"
              >
                <XCircle className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={handleConnect}
            disabled={connecting}
            size="sm"
            className={`mt-auto w-full text-white font-body ${color}`}
          >
            {connecting ? "Connecting..." : "Connect"}
          </Button>
        )}
      </div>
    </div>
  );
};

// VAT Scheme Card Component
const VATSchemeCard = ({ scheme, title, description, icon, active, vatImpact, onSelect }) => {
  return (
    <div 
      className={`text-center p-4 rounded-lg cursor-pointer transition-all border-2 ${
        active 
          ? 'bg-purple-50 border-purple-500' 
          : 'bg-gray-50 border-gray-200 hover:border-gray-300 hover:bg-gray-100'
      }`}
      onClick={onSelect}
    >
      <div className={`mx-auto mb-2 ${active ? 'text-purple-600' : 'text-gray-600'}`}>
        {icon}
      </div>
      <h4 className={`font-semibold font-heading ${active ? 'text-purple-900' : 'text-gray-700'}`}>
        {title}
      </h4>
      <p className={`text-xs font-body ${active ? 'text-purple-700' : 'text-gray-600'}`}>
        {description}
      </p>
      <div className="mt-3">
        {active ? (
          <Badge className="bg-purple-600 text-white text-xs">Active</Badge>
        ) : (
          <Button variant="ghost" size="sm" className="text-gray-600 text-xs">Select</Button>
        )}
      </div>
      <p className={`text-xs mt-2 font-body ${active ? 'text-purple-600' : 'text-gray-500'}`}>
        {vatImpact}
      </p>
    </div>
  );
};

// VAT Vault Page - Comprehensive VAT Management

const VATVaultPage = () => {
  const [vatData, setVatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [showExternalInvoiceForm, setShowExternalInvoiceForm] = useState(false);
  const [showCSVImportForm, setShowCSVImportForm] = useState(false);
  const [addOnStatus, setAddOnStatus] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const { token, user, logout } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    checkAddOnStatus();
  }, []);

  useEffect(() => {
    // Check for subscription success/cancel from URL params
    const subscriptionResult = searchParams.get('subscription');
    if (subscriptionResult === 'success') {
      toast({
        title: "Welcome to VAT Vault!",
        description: "Your subscription is now active. Enjoy comprehensive VAT tracking!"
      });
      // Refresh add-on status
      checkAddOnStatus();
    } else if (subscriptionResult === 'cancelled') {
      toast({
        title: "Subscription Cancelled",
        description: "You can subscribe anytime to access VAT Vault features.",
        variant: "destructive"
      });
    }
  }, [searchParams]);

  const checkAddOnStatus = async () => {
    try {
      const response = await axios.get(`${API}/add-ons/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddOnStatus(response.data);
      
      // If VAT Vault is enabled, fetch VAT data
      if (response.data.vat_vault_enabled) {
        fetchVATData();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to check add-on status:', error);
      // Fallback: try to fetch VAT data anyway (for test users)
      fetchVATData();
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/add-ons/vat-vault/subscribe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to start subscription. Please try again.",
        variant: "destructive"
      });
      setSubscribing(false);
    }
  };

  const fetchVATData = async () => {
    try {
      // This will fetch VAT data from various sources
      const response = await axios.get(`${API}/vat-vault`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVatData(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch VAT data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateVATScheme = async (newScheme) => {
    try {
      await axios.post(`${API}/settings/vat-scheme`, {
        vat_scheme: newScheme
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "VAT Scheme Updated",
        description: `Switched to ${newScheme === 'cash' ? 'Cash Accounting' : newScheme === 'invoice' ? 'Invoice Accounting' : 'Flat Rate Scheme'}. VAT calculations will be updated accordingly.`
      });
      
      // Refresh data to reflect new scheme
      fetchVATData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update VAT scheme",
        variant: "destructive"
      });
    }
  };

  // Use actual VAT data from API - proper mapping to backend fields
  // The headline "Total VAT" reflects VAT actually secured (paid), which is
  // the meaningful figure for a business owner setting aside tax money.
  // "Outstanding" = pending + overdue (what's still expected to come in).
  const vatStats = {
    totalVAT: vatData?.paid_vat || 0,
    paidVAT: vatData?.paid_vat || 0,
    pendingVAT: vatData?.pending_vat || 0,
    overdueVAT: vatData?.overdue_vat || 0,
    outstandingVAT: (vatData?.pending_vat || 0) + (vatData?.overdue_vat || 0),
    // Also track source breakdown
    paifyVAT: vatData?.paidd_vat || 0,
    externalVAT: vatData?.external_vat || 0,
    lastSweep: '15 Dec 2024', // TODO: Add actual sweep tracking
    nextSweep: '15 Jan 2025'   // TODO: Add actual sweep tracking
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="vat-vault" />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-body">Loading VAT Vault...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show subscription purchase screen if VAT Vault is not enabled
  if (addOnStatus && !addOnStatus.vat_vault_enabled) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="vat-vault" />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <PiggyBank className="w-10 h-10 text-purple-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4 font-heading">VAT Vault</h1>
            <p className="text-xl text-gray-600 font-body max-w-2xl mx-auto">
              Comprehensive VAT tracking and management. Never be caught short at tax time again.
            </p>
          </div>

          <Card className="border-0 shadow-lg mb-8">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 font-heading">What's Included</h2>
                  <ul className="space-y-4">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-body">Auto-allocate VAT from every payment into a dedicated pot</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-body">Real-time VAT tracking across all invoices and payments</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-body">Monthly VAT summaries and export to CSV</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-body">Support for Cash, Invoice, and Flat Rate VAT schemes</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-body">Manual entry for external invoices</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-body">HMRC-compliant record keeping</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-purple-50 rounded-xl p-6">
                  <div className="text-center">
                    <Badge className="bg-purple-100 text-purple-700 mb-4">Add-on</Badge>
                    <p className="text-4xl font-bold text-gray-900 font-heading mb-2">£9.99</p>
                    <p className="text-gray-600 font-body mb-6">per month</p>
                    
                    <Button
                      onClick={handleSubscribe}
                      disabled={subscribing}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-body py-6 text-lg"
                    >
                      {subscribing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Setting up...
                        </>
                      ) : (
                        <>
                          <PiggyBank className="w-5 h-5 mr-2" />
                          Subscribe to VAT Vault
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-gray-500 mt-4 font-body">
                      Cancel anytime. You'll keep access until the end of your billing period.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="text-center text-gray-500 text-sm font-body">
            <p>Secure payment powered by Stripe. Questions? Contact support@paify.io</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="vat-vault" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">VAT Vault</h1>
              <p className="text-gray-600 font-body">Comprehensive VAT tracking and management across all your business activities.</p>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className="bg-purple-100 text-purple-700">Cash Accounting</Badge>
              <Button 
                onClick={() => setShowExternalInvoiceForm(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-body"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add External Invoice
              </Button>
            </div>
          </div>
        </div>

        {/* VAT Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-8">
          <Card className="border-0 shadow-sm" data-testid="vat-stat-secured">
            <CardContent className="p-6 text-center">
              <PiggyBank className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">£{Math.round(vatStats.paidVAT).toLocaleString()}</p>
              <p className="text-sm text-gray-600 font-body">VAT Secured</p>
              <p className="text-[10px] text-gray-400 font-body mt-1">From paid payments only</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="vat-stat-outstanding">
            <CardContent className="p-6 text-center">
              <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">£{Math.round(vatStats.outstandingVAT).toLocaleString()}</p>
              <p className="text-sm text-gray-600 font-body">Outstanding</p>
              <p className="text-[10px] text-gray-400 font-body mt-1">Pending + overdue</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="vat-stat-pending">
            <CardContent className="p-6 text-center">
              <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">£{Math.round(vatStats.pendingVAT).toLocaleString()}</p>
              <p className="text-sm text-gray-600 font-body">Pending</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm" data-testid="vat-stat-overdue">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">£{Math.round(vatStats.overdueVAT).toLocaleString()}</p>
              <p className="text-sm text-gray-600 font-body">Overdue</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <RefreshCw className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900 font-heading">{vatStats.lastSweep}</p>
              <p className="text-sm text-gray-600 font-body">Last Sweep</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <Calendar className="w-8 h-8 text-[#e6007e] mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900 font-heading">{vatStats.nextSweep}</p>
              <p className="text-sm text-gray-600 font-body">Next Target</p>
            </CardContent>
          </Card>
        </div>

        {/* VAT Input Methods */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card 
            className="border-0 shadow-lg hover:shadow-xl transition-all cursor-pointer bg-gradient-to-br from-blue-50 to-blue-100"
            onClick={() => setActiveView('paidd')}
          >
            <CardContent className="p-6 text-center">
              <div className="p-3 bg-blue-600 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <Banknote className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 font-heading">Paify Payments</h3>
              <p className="text-sm text-gray-600 font-body">Auto-calculated from your Paify invoices and payments</p>
              <div className="mt-3 text-blue-600 font-heading">£{vatStats.paifyVAT.toLocaleString()}</div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-lg hover:shadow-xl transition-all cursor-pointer bg-gradient-to-br from-green-50 to-green-100"
            onClick={() => setShowExternalInvoiceForm(true)}
          >
            <CardContent className="p-6 text-center">
              <div className="p-3 bg-green-600 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <Upload className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 font-heading">Manual Entry</h3>
              <p className="text-sm text-gray-600 font-body">Add external invoices and track VAT manually</p>
              <div className="mt-3 text-green-600 font-heading">£{vatStats.externalVAT.toLocaleString()}</div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-lg hover:shadow-xl transition-all cursor-pointer bg-gradient-to-br from-yellow-50 to-yellow-100"
            onClick={() => setShowCSVImportForm(true)}
          >
            <CardContent className="p-6 text-center">
              <div className="p-3 bg-yellow-600 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 font-heading">CSV Import</h3>
              <p className="text-sm text-gray-600 font-body">Bulk import from accounting software</p>
              <div className="mt-3 text-yellow-600 font-heading">{vatData?.external_invoices_count || 0} invoices</div>
            </CardContent>
          </Card>
        </div>

        {/* Accounting Software Connections */}
        <Card className="border-0 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 font-heading">
              <Link className="w-5 h-5" />
              <span>Connect Accounting Software</span>
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Coming Soon
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {/* Coming Soon Overlay */}
              <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
                <div className="text-center p-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <p className="font-semibold text-gray-900 font-heading">Coming Soon</p>
                  <p className="text-sm text-gray-600 font-body mt-1">Accounting integrations are on the way!</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 opacity-50">
              <AccountingConnectionCard 
                provider="xero"
                name="Xero"
                description="Connect your Xero account for automatic VAT sync"
                color="bg-blue-600"
                connected={false}
              />
              <AccountingConnectionCard 
                provider="quickbooks"
                name="QuickBooks"
                description="Sync QuickBooks invoices and VAT data"
                color="bg-green-600"
                connected={false}
              />
              <AccountingConnectionCard 
                provider="freeagent"
                name="FreeAgent"
                description="Import FreeAgent VAT transactions"
                color="bg-purple-600"
                connected={false}
              />
              <AccountingConnectionCard 
                provider="sage"
                name="Sage"
                description="Connect Sage for comprehensive VAT tracking"
                color="bg-yellow-600"
                connected={false}
              />
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start space-x-2">
                <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 font-heading">Secure Integration</h4>
                  <p className="text-sm text-blue-700 font-body">
                    OAuth2 authentication • Read-only access • HMRC compliant • Real-time sync via webhooks
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 font-heading">
              <Settings className="w-5 h-5" />
              <span>VAT Scheme Settings</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6">
              <VATSchemeCard 
                scheme="cash"
                title="Cash Accounting"
                description="VAT due when payments received"
                icon={<Shield className="w-8 h-8" />}
                active={user?.vat_scheme === 'cash'}
                vatImpact="Lower cash flow impact - pay VAT only on money received"
                onSelect={() => updateVATScheme('cash')}
              />
              <VATSchemeCard 
                scheme="invoice"
                title="Invoice Accounting"
                description="VAT due when invoices issued"
                icon={<FileText className="w-8 h-8" />}
                active={user?.vat_scheme === 'invoice'}
                vatImpact="Standard scheme - VAT due on invoice issue date"
                onSelect={() => updateVATScheme('invoice')}
              />
              <VATSchemeCard 
                scheme="flat_rate"
                title="Flat Rate Scheme"
                description="Fixed % of gross turnover"
                icon={<Award className="w-8 h-8" />}
                active={user?.vat_scheme === 'flat_rate'}
                vatImpact="Simplified VAT - fixed percentage based on business type"
                onSelect={() => updateVATScheme('flat_rate')}
              />
            </div>
            
            {/* Scheme Impact Warning */}
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-800 font-heading">Important</h4>
                  <p className="text-sm text-yellow-700 font-body">
                    Changing VAT scheme affects how VAT is calculated and when it's due. 
                    Past figures remain as previously calculated. Consult your accountant before switching schemes.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* VAT Ledger */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center justify-between font-heading">
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5" />
                <span>VAT Ledger</span>
                <Badge className="bg-blue-100 text-blue-700">Live Tracking</Badge>
              </div>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    const csvData = generateVATExport(vatData);
                    downloadCSV(csvData, `vat-export-${new Date().toISOString().split('T')[0]}.csv`);
                    toast({
                      title: "VAT Export Ready",
                      description: "VAT ledger exported to CSV for HMRC submission"
                    });
                  }}
                  className="font-body"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    toast({
                      title: "VAT Reconciliation",
                      description: "VAT positions reconciled with current scheme settings"
                    });
                  }}
                  className="font-body"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reconcile
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <VATLedgerView vatData={vatData} user={user} />
          </CardContent>
        </Card>
      </main>

      {/* External Invoice Form Modal */}
      {showExternalInvoiceForm && (
        <ExternalInvoiceForm 
          isOpen={showExternalInvoiceForm}
          onClose={() => setShowExternalInvoiceForm(false)}
          onSuccess={() => {
            setShowExternalInvoiceForm(false);
            fetchVATData();
          }}
        />
      )}

      {/* CSV Import Form Modal */}
      {showCSVImportForm && (
        <CSVImportForm 
          isOpen={showCSVImportForm}
          onClose={() => setShowCSVImportForm(false)}
          onSuccess={() => {
            setShowCSVImportForm(false);
            fetchVATData();
          }}
        />
      )}
    </div>
  );
};

// VAT Ledger View Component - Event-based VAT tracking

const VATLedgerView = ({ vatData, user }) => {
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState('current_month');
  const [ledgerEvents, setLedgerEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  // Fetch VAT ledger events from backend
  useEffect(() => {
    const fetchLedgerEvents = async () => {
      try {
        const response = await axios.get(`${API}/vat-vault/ledger`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLedgerEvents(response.data.events || []);
      } catch (error) {
        console.error('Failed to fetch VAT ledger events:', error);
        setLedgerEvents([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchLedgerEvents();
    }
  }, [token]);

  // Filter events based on selected filter
  const filteredEvents = ledgerEvents.filter(event => {
    if (filter === 'accrued') return event.status === 'accrued';
    if (filter === 'pending') return event.status === 'awaiting_payment';
    if (filter === 'paidd') return event.source && event.source.includes('Paify');
    if (filter === 'external') return event.source && event.source.includes('External');
    return true;
  });

  const currentVATBalance = ledgerEvents
    .filter(e => e.status === 'accrued')
    .reduce((sum, e) => sum + (e.vat_amount || 0), 0);

  const getEventIcon = (type) => {
    switch (type) {
      case 'invoice_issued': return <FileText className="w-4 h-4 text-blue-600" />;
      case 'payment_received': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'external_invoice': return <Upload className="w-4 h-4 text-purple-600" />;
      case 'external_payment': return <Banknote className="w-4 h-4 text-green-600" />;
      case 'credit_note': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <Circle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getSchemeIndicator = (event) => {
    if (event.scheme_impact === 'immediate') {
      return <Badge className="bg-green-100 text-green-700 text-xs">VAT Due</Badge>;
    } else if (event.scheme_impact === 'deferred') {
      return <Badge className="bg-yellow-100 text-yellow-700 text-xs">Awaiting Payment</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-700 text-xs">Processed</Badge>;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
        <p className="text-gray-600 font-body">Loading VAT ledger...</p>
      </div>
    );
  }

  if (ledgerEvents.length === 0) {
    return (
      <div className="text-center py-12">
        <PiggyBank className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">VAT Vault Ready</h3>
        <p className="text-gray-600 font-body">
          Start adding external invoices or let Paify payments automatically populate your VAT tracking.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* VAT Scheme Logic Info */}
      <div className="bg-purple-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-purple-900 font-heading">
            Active Scheme: {user?.vat_scheme || 'Cash Accounting'}
          </h4>
          <div className="text-purple-900 font-heading">
            Current VAT Balance: £{currentVATBalance.toFixed(2)}
          </div>
        </div>
        <p className="text-sm text-purple-700 font-body">
          {user?.vat_scheme === 'invoice' ? 
            'VAT accrues immediately when invoices are issued (invoice accounting)' :
            user?.vat_scheme === 'flat_rate' ?
            'VAT calculated as fixed percentage of gross turnover (flat rate scheme)' :
            'VAT accrues only when payments are received (cash accounting)'
          }
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4 mb-4">
        <div className="flex rounded-md shadow-sm">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 text-sm font-medium rounded-l-md border font-body ${
              filter === 'all' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            All Events
          </button>
          <button
            onClick={() => setFilter('accrued')}
            className={`px-3 py-1 text-sm font-medium border-t border-b font-body ${
              filter === 'accrued' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            VAT Due
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1 text-sm font-medium border-t border-b font-body ${
              filter === 'pending' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('paidd')}
            className={`px-3 py-1 text-sm font-medium border-t border-b font-body ${
              filter === 'paidd' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Paify
          </button>
          <button
            onClick={() => setFilter('external')}
            className={`px-3 py-1 text-sm font-medium rounded-r-md border font-body ${
              filter === 'external' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            External
          </button>
        </div>
        
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="current_month">This Month</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="current_quarter">This Quarter</SelectItem>
            <SelectItem value="all_time">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* VAT Events List */}
      <div className="space-y-2">
        {filteredEvents.map((event) => (
          <div key={event.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {getEventIcon(event.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <h4 className="text-sm font-medium text-gray-900 font-heading">{event.description}</h4>
                  {getSchemeIndicator(event)}
                </div>
                <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500 font-body">
                  <span>{event.date ? new Date(event.date).toLocaleDateString() : 'N/A'}</span>
                  <span>•</span>
                  <span>{event.client || 'Unknown'}</span>
                  <span>•</span>
                  <span>{event.source}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium font-heading text-purple-600">
                +£{Math.max(0, event.vat_amount || 0).toFixed(2)}
              </div>
              <div className="text-xs text-gray-500 font-body">
                Net: £{Math.max(0, event.net_amount || 0).toFixed(2)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Scheme Logic Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-2 font-heading">VAT Scheme Logic Applied</h4>
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div>
            <span className="text-gray-600 font-body">Invoice Events:</span>
            <div className="font-medium text-gray-900 font-body">
              {user?.vat_scheme === 'invoice' ? 'VAT due immediately' : 'VAT deferred until payment'}
            </div>
          </div>
          <div>
            <span className="text-gray-600 font-body">Payment Events:</span>
            <div className="font-medium text-gray-900 font-body">
              {user?.vat_scheme === 'cash' ? 'VAT accrues on receipt' : 'Already accrued'}
            </div>
          </div>
          <div>
            <span className="text-gray-600 font-body">Current Balance:</span>
            <div className="font-medium text-purple-600 font-body">£{currentVATBalance.toFixed(2)} due</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions for VAT export and scheme logic

const generateVATExport = (vatData) => {
  const headers = ['Date', 'Type', 'Reference', 'Description', 'Net Amount', 'VAT Rate', 'VAT Amount', 'Status', 'Scheme Impact'];
  
  // Return empty data for new accounts
  // TODO: Backend should provide transaction history to export
  const rows = [];
  
  return [headers, ...rows];
};

const downloadCSV = (csvData, filename) => {
  const csvContent = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

// Professional CSV Import Component with Bulletproof Validation

const CSVImportForm = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Validate, 4: Import
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mappings, setMappings] = useState({});
  const [validationResults, setValidationResults] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  // Required fields for VAT import
  const requiredFields = [
    { key: 'invoice_date', label: 'Invoice Date', required: true },
    { key: 'invoice_number', label: 'Invoice Number', required: true },
    { key: 'client_name', label: 'Client Name', required: false },
    { key: 'net_amount', label: 'Net Amount', required: false },
    { key: 'gross_amount', label: 'Gross Amount', required: false },
    { key: 'vat_rate', label: 'VAT Rate (%)', required: true },
    { key: 'vat_amount', label: 'VAT Amount', required: false },
    { key: 'payment_status', label: 'Payment Status', required: false }
  ];

  // Accounting software templates
  const softwareTemplates = {
    xero: {
      name: 'Xero Export',
      mappings: {
        'invoice_date': 'Date',
        'invoice_number': 'Invoice Number', 
        'client_name': 'Contact',
        'gross_amount': 'Total',
        'vat_amount': 'Tax Amount',
        'payment_status': 'Status'
      }
    },
    quickbooks: {
      name: 'QuickBooks Export',
      mappings: {
        'invoice_date': 'Date',
        'invoice_number': 'Num',
        'client_name': 'Customer',
        'gross_amount': 'Amount',
        'vat_amount': 'Tax',
        'payment_status': 'Paid'
      }
    },
    freeagent: {
      name: 'FreeAgent Export',
      mappings: {
        'invoice_date': 'Invoice Date',
        'invoice_number': 'Reference',
        'client_name': 'Contact Name',
        'net_amount': 'Net Total',
        'vat_amount': 'Sales Tax Total',
        'gross_amount': 'Gross Total'
      }
    },
    generic: {
      name: 'Generic CSV',
      mappings: {}
    }
  };

  // Parse CSV file
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast({
        title: "Invalid File",
        description: "Please upload a CSV file",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast({
        title: "File Too Large",
        description: "Please upload files smaller than 10MB",
        variant: "destructive"
      });
      return;
    }

    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast({
          title: "Invalid CSV",
          description: "CSV must have at least a header row and one data row",
          variant: "destructive"
        });
        return;
      }

      const csvHeaders = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const csvRows = lines.slice(1).map(line => 
        line.split(',').map(cell => cell.replace(/"/g, '').trim())
      );

      if (csvRows.length > 500) {
        toast({
          title: "File Too Large", 
          description: "Maximum 500 invoices per import. Please split your file.",
          variant: "destructive"
        });
        return;
      }

      setHeaders(csvHeaders);
      setCsvData(csvRows);
      setStep(2);
    };
    reader.readAsText(file);
  };

  // Apply software template mapping
  const applyTemplate = (templateKey) => {
    const template = softwareTemplates[templateKey];
    const newMappings = {};
    
    Object.entries(template.mappings).forEach(([field, csvColumn]) => {
      const headerIndex = headers.findIndex(h => h.toLowerCase().includes(csvColumn.toLowerCase()));
      if (headerIndex !== -1) {
        newMappings[field] = headers[headerIndex];
      }
    });
    
    setMappings(newMappings);
    toast({
      title: "Template Applied",
      description: `${template.name} mapping applied. Review and adjust as needed.`
    });
  };

  // Validate mapped data
  const validateCSVData = () => {
    const errors = [];
    const warnings = [];
    const duplicates = [];
    const validRows = [];

    csvData.forEach((row, index) => {
      const rowData = {};
      const rowErrors = [];

      // Map columns to fields
      Object.entries(mappings).forEach(([field, csvColumn]) => {
        const columnIndex = headers.indexOf(csvColumn);
        if (columnIndex !== -1) {
          rowData[field] = row[columnIndex];
        }
      });

      // Validate required fields
      if (!rowData.invoice_date) {
        rowErrors.push(`Row ${index + 1}: Missing invoice date`);
      } else if (isNaN(Date.parse(rowData.invoice_date))) {
        rowErrors.push(`Row ${index + 1}: Invalid date format`);
      }

      if (!rowData.invoice_number) {
        rowErrors.push(`Row ${index + 1}: Missing invoice number`);
      }

      if (!rowData.vat_rate && !rowData.vat_amount) {
        rowErrors.push(`Row ${index + 1}: Must have either VAT rate or VAT amount`);
      }

      if (!rowData.net_amount && !rowData.gross_amount) {
        rowErrors.push(`Row ${index + 1}: Must have either net amount or gross amount`);
      }

      // Check for duplicates
      if (validRows.some(r => r.invoice_number === rowData.invoice_number)) {
        duplicates.push(`Row ${index + 1}: Duplicate invoice number ${rowData.invoice_number}`);
      }

      if (rowErrors.length === 0) {
        validRows.push({ ...rowData, rowIndex: index + 1 });
      } else {
        errors.push(...rowErrors);
      }
    });

    setValidationResults({
      errors,
      warnings,
      duplicates,
      validRows,
      totalRows: csvData.length,
      validCount: validRows.length
    });

    setStep(3);
  };

  // Process import
  const handleImport = async () => {
    if (!validationResults || validationResults.errors.length > 0) {
      toast({
        title: "Cannot Import",
        description: "Please fix all validation errors before importing",
        variant: "destructive"
      });
      return;
    }

    setImporting(true);
    setImportProgress(0);

    try {
      // Process in chunks for large imports
      const chunkSize = validationResults.validRows.length > 100 ? 50 : validationResults.validRows.length;
      const chunks = [];
      
      for (let i = 0; i < validationResults.validRows.length; i += chunkSize) {
        chunks.push(validationResults.validRows.slice(i, i + chunkSize));
      }

      let importedCount = 0;
      let skippedCount = 0;

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        
        try {
          const response = await axios.post(`${API}/vat-vault/csv-import`, {
            invoices: chunk,
            import_settings: {
              conflict_resolution: 'skip_duplicates',
              currency: 'GBP',
              vat_scheme: user?.vat_scheme || 'cash'
            }
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const result = response.data;
          importedCount += result.imported_count || 0;
          skippedCount += result.skipped_count || 0;
          
        } catch (error) {
          toast({
            title: "Import Error", 
            description: `Failed to import chunk ${chunkIndex + 1}`,
            variant: "destructive"
          });
        }

        // Update progress
        setImportProgress(((chunkIndex + 1) / chunks.length) * 100);
      }

      toast({
        title: "Import Complete",
        description: `Successfully imported ${importedCount} invoices. ${skippedCount > 0 ? `Skipped ${skippedCount} duplicates.` : ''}`
      });

      setStep(4);
      setTimeout(() => {
        onSuccess();
      }, 2000);

    } catch (error) {
      toast({
        title: "Import Failed",
        description: "Failed to complete CSV import",
        variant: "destructive"
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">CSV Import - Professional VAT Data Import</DialogTitle>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center space-x-4 mb-6">
          {[1, 2, 3, 4].map((stepNumber) => (
            <div key={stepNumber} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= stepNumber ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {stepNumber}
              </div>
              <span className={`ml-2 text-sm font-body ${step >= stepNumber ? 'text-purple-600' : 'text-gray-500'}`}>
                {stepNumber === 1 ? 'Upload' : stepNumber === 2 ? 'Map' : stepNumber === 3 ? 'Validate' : 'Import'}
              </span>
              {stepNumber < 4 && <ArrowRight className="w-4 h-4 text-gray-400 mx-4" />}
            </div>
          ))}
        </div>

        {/* Step 1: File Upload */}
        {step === 1 && (
          <div className="space-y-6">
            <Card className="border-2 border-dashed border-purple-200 bg-purple-50">
              <CardContent className="p-8 text-center">
                <Upload className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">Upload Your VAT Data</h3>
                <p className="text-gray-600 font-body mb-4">
                  Upload CSV exports from Xero, QuickBooks, FreeAgent, or any CSV with invoice data
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <Label 
                  htmlFor="csv-upload"
                  className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg cursor-pointer hover:bg-purple-700 font-body"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose CSV File
                </Label>
                <p className="text-xs text-gray-500 mt-3 font-body">
                  Maximum 500 invoices per import • 10MB file size limit
                </p>
              </CardContent>
            </Card>

            {/* Software Templates */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(softwareTemplates).map(([key, template]) => (
                <div key={key} className="text-center p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                  <div className="text-gray-600 mb-2">
                    <FileText className="w-6 h-6 mx-auto" />
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm font-heading">{template.name}</h4>
                  <p className="text-xs text-gray-600 font-body">Pre-configured mapping</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Column Mapping */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 font-heading">Map Your Columns</h3>
              <div className="flex space-x-2">
                {Object.entries(softwareTemplates).map(([key, template]) => (
                  <Button
                    key={key}
                    variant="outline"
                    size="sm"
                    onClick={() => applyTemplate(key)}
                    className="font-body"
                  >
                    {template.name}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* CSV Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-md font-heading">Your CSV Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 rounded p-4 max-h-64 overflow-auto">
                    <div className="text-xs font-mono space-y-1">
                      <div className="font-bold text-gray-700 border-b pb-1">
                        {headers.join(' | ')}
                      </div>
                      {csvData.slice(0, 5).map((row, i) => (
                        <div key={i} className="text-gray-600">
                          {row.join(' | ')}
                        </div>
                      ))}
                      {csvData.length > 5 && (
                        <div className="text-gray-400 text-center pt-2">
                          ... {csvData.length - 5} more rows
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Field Mapping */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-md font-heading">Map to VAT Fields</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {requiredFields.map((field) => (
                      <div key={field.key}>
                        <Label className={`text-sm font-body ${field.required ? 'text-gray-900' : 'text-gray-600'}`}>
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </Label>
                        <Select 
                          value={mappings[field.key] || ''} 
                          onValueChange={(value) => setMappings({...mappings, [field.key]: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select column..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">-- Not mapped --</SelectItem>
                            {headers.map((header) => (
                              <SelectItem key={header} value={header}>{header}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    onClick={validateCSVData}
                    disabled={!mappings.invoice_date || !mappings.invoice_number || !mappings.vat_rate}
                    className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white font-body"
                  >
                    Validate Data
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 3: Validation Results */}
        {step === 3 && validationResults && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 font-heading">Validation Results</h3>
            
            {/* Validation Summary */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-green-600 font-heading">{validationResults.validCount}</p>
                  <p className="text-xs text-gray-600 font-body">Valid Rows</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <XCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-red-600 font-heading">{validationResults.errors.length}</p>
                  <p className="text-xs text-gray-600 font-body">Errors</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-yellow-600 font-heading">{validationResults.duplicates.length}</p>
                  <p className="text-xs text-gray-600 font-body">Duplicates</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <FileText className="w-6 h-6 text-gray-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-gray-600 font-heading">{validationResults.totalRows}</p>
                  <p className="text-xs text-gray-600 font-body">Total Rows</p>
                </CardContent>
              </Card>
            </div>

            {/* Error Details */}
            {validationResults.errors.length > 0 && (
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-red-700 font-heading">Errors Must Be Fixed</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-32 overflow-y-auto">
                    {validationResults.errors.map((error, i) => (
                      <p key={i} className="text-sm text-red-600 font-body">{error}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Duplicate Warning */}
            {validationResults.duplicates.length > 0 && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardHeader>
                  <CardTitle className="text-yellow-700 font-heading">Duplicates Will Be Skipped</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-32 overflow-y-auto">
                    {validationResults.duplicates.map((duplicate, i) => (
                      <p key={i} className="text-sm text-yellow-600 font-body">{duplicate}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Valid Data Preview */}
            {validationResults.validCount > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-green-700 font-heading">Ready to Import ({validationResults.validCount} invoices)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {validationResults.validRows.slice(0, 3).map((row, i) => (
                      <div key={i} className="p-3 bg-green-50 rounded text-sm">
                        <div className="font-medium text-green-900 font-body">{row.invoice_number} - {row.client_name}</div>
                        <div className="text-green-700 text-xs font-body">
                          Date: {row.invoice_date} • VAT: £{row.vat_amount || 'TBD'} • Rate: {row.vat_rate}%
                        </div>
                      </div>
                    ))}
                    {validationResults.validCount > 3 && (
                      <p className="text-xs text-gray-500 text-center font-body">
                        ... and {validationResults.validCount - 3} more invoices
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} className="font-body">
                Back to Mapping
              </Button>
              <Button
                onClick={handleImport}
                disabled={validationResults.errors.length > 0 || importing}
                className="bg-green-600 hover:bg-green-700 text-white font-body"
              >
                {importing ? "Importing..." : `Import ${validationResults.validCount} Invoices`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Import Complete */}
        {step === 4 && (
          <div className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading">Import Successful!</h3>
            <p className="text-gray-600 font-body">
              Your VAT data has been imported and is now available in the VAT Vault ledger.
            </p>
            
            {importing && (
              <div className="mt-6">
                <div className="bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${importProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 font-body">Processing... {Math.round(importProgress)}%</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ExternalInvoiceForm = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    client_name: '',
    client_email: '',
    vat_scheme: 'cash',
    entry_method: 'breakdown', // 'breakdown' or 'gross_rate'
    // Line items for breakdown method
    line_items: [
      {
        id: 1,
        description: 'Service',
        net_amount: 0,
        vat_rate: 20,
        vat_amount: 0,
        gross_amount: 0
      }
    ],
    // Gross + rate method
    gross_amount: 0,
    overall_vat_rate: 20,
    // Status tracking
    payment_status: 'unpaid',
    payment_date: '',
    notes: ''
  });

  const [saving, setSaving] = useState(false);
  // VAT is always enabled for external invoice form since it's for VAT tracking
  const vatEnabled = true;
  const { token } = useAuth();
  const { toast } = useToast();

  // Calculate amounts based on entry method
  const calculateAmounts = () => {
    if (formData.entry_method === 'breakdown') {
      // Calculate from line items
      const totals = formData.line_items.reduce((acc, item) => {
        const net = parseFloat(item.net_amount) || 0;
        const vat = net * (item.vat_rate / 100);
        const gross = net + vat;
        return {
          net: acc.net + net,
          vat: acc.vat + vat,
          gross: acc.gross + gross
        };
      }, { net: 0, vat: 0, gross: 0 });
      return totals;
    } else {
      // Back-calculate from gross + rate
      const gross = parseFloat(formData.gross_amount) || 0;
      const rate = parseFloat(formData.overall_vat_rate) || 0;
      const vat = gross * (rate / (100 + rate));
      const net = gross - vat;
      return { net, vat, gross };
    }
  };

  const totals = calculateAmounts();

  const updateLineItem = (index, field, value) => {
    const updatedItems = [...formData.line_items];
    updatedItems[index][field] = value;
    
    // Auto-calculate VAT and gross for this line
    if (field === 'net_amount' || field === 'vat_rate') {
      const net = parseFloat(updatedItems[index].net_amount) || 0;
      const vatRate = parseFloat(updatedItems[index].vat_rate) || 0;
      const vat = net * (vatRate / 100);
      const gross = net + vat;
      
      updatedItems[index].vat_amount = vat;
      updatedItems[index].gross_amount = gross;
    }
    
    setFormData({ ...formData, line_items: updatedItems });
  };

  const addLineItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      net_amount: 0,
      vat_rate: 20,
      vat_amount: 0,
      gross_amount: 0
    };
    setFormData({ ...formData, line_items: [...formData.line_items, newItem] });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length === 1) {
      toast({
        title: "Cannot Remove",
        description: "Invoice must have at least one line item",
        variant: "destructive"
      });
      return;
    }
    const updatedItems = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: updatedItems });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API}/vat-vault/external-invoice`, {
        ...formData,
        calculated_totals: totals
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "External Invoice Added",
        description: "Invoice added to VAT Vault successfully"
      });

      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add external invoice",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Add External Invoice</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSave} className="space-y-6">
          {/* Basic Invoice Info */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Invoice Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Invoice Number</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                    placeholder="INV-EXT-001"
                    required
                    className="font-body"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Invoice Date</Label>
                  <Input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
                    required
                    className="font-body"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">VAT Scheme</Label>
                  <Select value={formData.vat_scheme} onValueChange={(value) => setFormData({...formData, vat_scheme: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash Accounting</SelectItem>
                      <SelectItem value="invoice">Invoice Accounting</SelectItem>
                      <SelectItem value="flat_rate">Flat Rate Scheme</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Client Name</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    placeholder="Client Business Name"
                    className="font-body"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Client Email (Optional)</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    placeholder="client@company.com"
                    className="font-body"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* VAT Entry Method */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">VAT Calculation Method</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, entry_method: 'breakdown'})}
                  className={`p-4 text-left border-2 rounded-lg transition-all ${
                    formData.entry_method === 'breakdown' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <h4 className="font-semibold text-gray-900 font-heading">Line Breakdown</h4>
                  <p className="text-sm text-gray-600 font-body">Enter net amounts and VAT rates per line item</p>
                </button>
                
                <button
                  type="button"
                  onClick={() => setFormData({...formData, entry_method: 'gross_rate'})}
                  className={`p-4 text-left border-2 rounded-lg transition-all ${
                    formData.entry_method === 'gross_rate' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <h4 className="font-semibold text-gray-900 font-heading">Gross + Rate</h4>
                  <p className="text-sm text-gray-600 font-body">Enter total amount and overall VAT rate</p>
                </button>
              </div>

              {formData.entry_method === 'breakdown' ? (
                /* Line Items Breakdown */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium font-heading">Invoice Line Items</h4>
                    <Button
                      type="button"
                      onClick={addLineItem}
                      variant="outline"
                      size="sm"
                      className="font-body"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Line
                    </Button>
                  </div>
                  
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 font-body">
                    <div className="col-span-5">Description</div>
                    <div className="col-span-2 text-right">Net Amount</div>
                    <div className="col-span-2 text-center">VAT Rate</div>
                    <div className="col-span-2 text-right">VAT Amount</div>
                    <div className="col-span-1 text-center">Actions</div>
                  </div>
                  
                  {/* Line Items */}
                  {formData.line_items.map((item, index) => (
                    <div key={item.id} className="grid grid-cols-12 gap-2 items-center p-3 bg-gray-50 rounded-lg">
                      <div className="col-span-5">
                        <Input
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          placeholder="Service description"
                          className="font-body text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={item.net_amount}
                          onChange={(e) => updateLineItem(index, 'net_amount', e.target.value)}
                          className="font-body text-sm text-right"
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                      <div className="col-span-2">
                        <Select 
                          value={vatEnabled ? item.vat_rate.toString() : "0"} 
                          onValueChange={(value) => updateLineItem(index, 'vat_rate', parseInt(value))}
                          disabled={!vatEnabled}
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0%</SelectItem>
                            {vatEnabled && (
                              <>
                                <SelectItem value="5">5%</SelectItem>
                                <SelectItem value="20">20%</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 text-right">
                        <span className="text-sm font-medium text-gray-600 font-body">£{item.vat_amount?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="col-span-1 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLineItem(index)}
                          className="text-red-600 hover:text-red-900 h-8 w-8 p-0"
                          disabled={formData.line_items.length === 1}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Gross + Rate Method */
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">Total Invoice Amount (Gross)</Label>
                    <Input
                      type="number"
                      value={formData.gross_amount}
                      onChange={(e) => setFormData({...formData, gross_amount: e.target.value})}
                      placeholder="1200.00"
                      step="0.01"
                      className="font-body"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">Overall VAT Rate (%)</Label>
                    <Select value={formData.overall_vat_rate.toString()} onValueChange={(value) => setFormData({...formData, overall_vat_rate: parseInt(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0% (Zero-rated)</SelectItem>
                        <SelectItem value="5">5% (Reduced rate)</SelectItem>
                        <SelectItem value="20">20% (Standard rate)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Calculated Totals */}
              <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-gray-200">
                <div className="bg-gray-100 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-500 font-body">Net Amount</p>
                  <p className="text-lg font-bold text-gray-900 font-heading">£{totals.net.toFixed(2)}</p>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg text-center">
                  <p className="text-xs text-purple-600 font-body">VAT Amount</p>
                  <p className="text-lg font-bold text-purple-900 font-heading">£{totals.vat.toFixed(2)}</p>
                </div>
                <div className="bg-green-100 p-3 rounded-lg text-center">
                  <p className="text-xs text-green-600 font-body">Gross Total</p>
                  <p className="text-lg font-bold text-green-900 font-heading">£{totals.gross.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Status */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Payment Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Payment Status</Label>
                  <Select value={formData.payment_status} onValueChange={(value) => setFormData({...formData, payment_status: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unpaid">Unpaid</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="partially_paid">Partially Paid</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.payment_status === 'paid' && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">Payment Date</Label>
                    <Input
                      type="date"
                      value={formData.payment_date}
                      onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                      className="font-body"
                    />
                  </div>
                )}
                
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Notes (Optional)</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes..."
                    className="font-body"
                  />
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700 font-body">
                  <strong>VAT Impact:</strong> This will add £{totals.vat.toFixed(2)} to your VAT Vault
                  {formData.vat_scheme === 'cash' && formData.payment_status === 'unpaid' && 
                    ' (will be added to vault when payment is received due to Cash Accounting scheme)'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose} className="font-body">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white font-body">
              {saving ? "Adding..." : "Add to VAT Vault"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Simple Quote Card Component for QuotesPage

export { AccountingConnectionCard, VATSchemeCard, VATVaultPage, VATLedgerView, generateVATExport, downloadCSV, CSVImportForm, ExternalInvoiceForm };
